import _, { last } from "lodash";
import moment from "moment";

function processListingParams(params) {
  const queryParams = new URLSearchParams(params);
  const url = new URL(window.location.href);

  const processedParams = {
    propertyDetails: {
      propertyId: null,
      propertyName: null,
    },

    location: {
      country: "Sri Lanka",
      province: null,
      district: null,
      city: null,
      label: null,
      latitude: null,
      longitude: null,
    },

    bookingDates: {
      checkinDate: moment().format("YYYY-MM-DD"),
      checkoutDate: moment().add(1, "days").format("YYYY-MM-DD"),
    },

    guests: {
      adultCount: 2,
      childCount: 0,
      childAges: [],
      roomCount: 1,
    },

    filters: {
      minPrice: null,
      maxPrice: null,

      minStarRating: null,
      maxStarRating: null,

      minReviewScore: null,
      maxReviewScore: null,

      cancelationPolicyTypes: [],

      meals: [],
      propertyTypeIds: [],
    },

    sortOptions: {
      by: null,
      order: null,
      type: null,
    },

    pagination: {
      limit: 20,
      page: 1,
    },

    searchOptions: {
      roomCountType: "suggestion",
    },
  };

  for (let [key, value] of queryParams.entries()) {
    // convert null and undefined values to null
    if (value === "null" || value === "undefined") {
      value = null;
    }

    switch (key) {
      case "checkin":
        if (!moment(value).isValid()) {
          break;
        }

        // check if the checkin date is before today
        if (moment(value).isBefore(moment().format("YYYY-MM-DD"))) {
          // Set the check-in date to today's date if the input date is before today
          const todayDate = moment().format("YYYY-MM-DD");
          processedParams.bookingDates.checkinDate = todayDate;

          url.searchParams.set(key, todayDate);
          window.history.replaceState(null, "", url.toString());
        } else {
          processedParams.bookingDates.checkinDate = value;
        }

        break;

      case "checkout":
        // In url checkout date is always after checkin date
        const checkinDate = moment(processedParams.bookingDates.checkinDate);

        if (!moment(value).isValid() || !checkinDate.isValid()) {
          break;
        }

        // Check if the checkout date is before or equal to the checkin date
        if (moment(value).isSameOrBefore(checkinDate.format("YYYY-MM-DD"))) {
          // Set the checkout date to tomorrow's date if the input date is before or equal to today
          const nextDate = checkinDate.add(1, "days").format("YYYY-MM-DD");
          processedParams.bookingDates.checkoutDate = nextDate;

          url.searchParams.set(key, nextDate);
          window.history.replaceState(null, "", url.toString());
        } else {
          processedParams.bookingDates.checkoutDate = value;
        }

        break;

      case "adults":
        processedParams.guests.adultCount = parseIntOrDefault(value, 2);
        break;

      case "children":
        processedParams.guests.childCount = parseIntOrDefault(value, 0);
        break;
      case "child-ages":
        if (!value) {
          processedParams.guests.childAges = [];
        } else {
          processedParams.guests.childAges = value.split(",").map((age) => parseIntOrDefault(age, 0));
        }

        break;

      case "rooms":
        processedParams.guests.roomCount = parseIntOrDefault(value, 1);
        break;

      case "property-id":
        processedParams.propertyDetails.propertyId = value ?? null;
        break;

      case "location":
        processedParams.location.label = value ? decodeURIComponent(value) : null;
        if (queryParams.has("property-id")) {
          processedParams.propertyDetails.propertyName = value ? decodeURIComponent(value) : null;
        }
        break;

      case "country":
        processedParams.location.country = value ?? "Sri Lanka";
        break;

      case "province":
        processedParams.location.province = value ?? null;
        break;

      case "district":
        processedParams.location.district = value ?? null;
        break;

      case "city":
        processedParams.location.city = value ?? null;
        break;

      case "latitude":
        processedParams.location.latitude = parseFloatOrDefault(value, null);
        break;

      case "longitude":
        processedParams.location.longitude = parseFloatOrDefault(value, null);
        break;

      // filters
      case "price":
        const [minPrice, maxPrice] = value.split("-");

        processedParams.filters.minPrice = parseFloatOrDefault(minPrice, null);
        processedParams.filters.maxPrice = parseFloatOrDefault(maxPrice, null);
        break;

      case "star":
        const [minStar, maxStar] = value.split("-");

        processedParams.filters.minStarRating = parseIntOrDefault(minStar, null);

        processedParams.filters.maxStarRating = parseIntOrDefault(maxStar, null);
        break;

      case "score":
        const [minReviewScore, maxReviewScore] = value.split("-");

        processedParams.filters.minReviewScore = parseIntOrDefault(minReviewScore, null);
        processedParams.filters.maxReviewScore = parseIntOrDefault(maxReviewScore, null);
        break;

      case "policy":
        if (value) {
          processedParams.filters.cancelationPolicyTypes = value.split(",").map((type) => (type === "F7D" ? "FLEXIBLE" : "NON_REFUNDABLE"));
        }

        break;

      case "meals":
        processedParams.filters.meals = mapMealsWithIds(value.split(","));
        break;

      case "property-types":
        processedParams.filters.propertyTypeIds =
          value
            .split(",")
            .map((id) => parseIntOrDefault(id, null))
            .filter(Boolean) ?? [];
        break;

      case "sortType":
        const { sortBy, sortOrder } = convertSortingValue(value);

        processedParams.sortOptions.by = sortBy;
        processedParams.sortOptions.order = sortOrder;
        processedParams.sortOptions.type = value;

        break;

      case "limit":
        processedParams.pagination.limit = parseIntOrDefault(value, 20);
        break;

      case "page":
        processedParams.pagination.page = parseIntOrDefault(value, 1);
        break;

      case "rcst":
        processedParams.searchOptions.roomCountType = value ?? "suggestion";
        break;

      default:
        break;
    }
  }

  return processedParams;
}

function generateListingUrl({
  params = {},
  searchCriteria = null,
  filters = {},
  searchOptions = {
    roomCountType: "suggestion",
  },
  sortType = null,
  pagination = null,
}) {
  // if searchCriteria is not provided, use the params object
  if (_.isNil(searchCriteria)) {
    searchCriteria = params;
  }

  // temp change
  if (!sortType) {
    sortType = "topReviewed";
  }

  if (_.isNil(pagination)) {
    pagination = params.pagination;
  }

  const { propertyDetails = {}, location = {}, bookingDates = {}, guests = {} } = searchCriteria;

  const propertyId = propertyDetails.propertyId;

  const country = location.country;
  const province = location.province ?? "";
  const district = location.district ?? "";
  const city = location.city ?? "";
  const label = location.label ?? "";
  const latitude = location.latitude ?? "";
  const longitude = location.longitude ?? "";

  const checkinDate = bookingDates.checkinDate;
  const checkoutDate = bookingDates.checkoutDate;

  const adultCount = guests.adultCount;
  const childCount = guests.childCount;
  const childAges = guests.childAges;

  const room = guests.roomCount;

  let filtersPart = "";

  const {
    minPrice,
    maxPrice,

    minStarRating,
    maxStarRating,

    minReviewScore,
    maxReviewScore,

    cancelationPolicyTypes = [],

    meals,

    propertyTypeIds = [],
  } = filters;

  if (maxPrice > 0) {
    filtersPart += `&price=${minPrice ?? 0}-${maxPrice}`;
  }

  if (maxStarRating) {
    filtersPart += `&star=${minStarRating ?? 0}-${maxStarRating}`;
  }

  if (maxReviewScore) {
    filtersPart += `&score=${minReviewScore ?? 0}-${maxReviewScore}`;
  }

  if (cancelationPolicyTypes && cancelationPolicyTypes.length > 0) {
    filtersPart += `&policy=${cancelationPolicyTypes.map((type) => (type === "FLEXIBLE" ? "F7D" : "NR")).join(",")}`;
  }

  if (meals && meals.length > 0) {
    filtersPart += `&meals=${mapIdsWithMeals(meals).join(",")}`;
  }

  if (propertyTypeIds && propertyTypeIds.length > 0) {
    filtersPart += `&property-types=${propertyTypeIds.join(",")}`;
  }

  const url = `/properties?location=${encodeURIComponent(label)}&${propertyId ? `property-id=${propertyId}&` : ""}country=${country}${
    province ? `&province=${province}` : ""
  }${district ? `&district=${district}` : ""}${
    city ? `&city=${city}` : ""
  }&latitude=${latitude}&longitude=${longitude}&checkin=${checkinDate}&checkout=${checkoutDate}&adults=${adultCount}&children=${childCount ?? 0}${
    childCount > 0 ? `&child-ages=${childAges.join(",")}` : ""
  }&rooms=${room}&rcst=${searchOptions.roomCountType}${filtersPart}${sortType ? `&sortType=${sortType}` : ""}&limit=${pagination.limit}&page=${
    pagination.page
  }${window.location.hash}`;

  return encodeURI(url);
}

export { processListingParams, generateListingUrl };

function convertSortingValue(value) {
  const mapping = {
    priceHigh: { sortBy: "price", sortOrder: "desc" },
    priceLow: { sortBy: "price", sortOrder: "asc" },
    starRatingHighest: {
      sortBy: "rating",
      sortOrder: "desc",
    },
    starRatingLowest: {
      sortBy: "rating",
      sortOrder: "asc",
    },
    topReviewed: {
      sortBy: "score",
      sortOrder: "desc",
    },
    default: { sortBy: null, sortOrder: null },
  };

  return mapping[value] ?? mapping.default;
}

function mapMealsWithIds(meals = []) {
  return (
    meals
      .map((meal) => {
        switch (meal) {
          case "Breakfast":
            return 1;
          case "Lunch":
            return 2;
          case "Dinner":
            return 3;
          default:
            return null;
        }
      })
      .filter(Boolean) ?? []
  );
}

function mapIdsWithMeals(ids = []) {
  return (
    ids
      .map((id) => {
        switch (id) {
          case 1:
            return "Breakfast";
          case 2:
            return "Lunch";
          case 3:
            return "Dinner";
          default:
            return null;
        }
      })
      .filter(Boolean) ?? []
  );
}

function parseIntOrDefault(value, defaultValue) {
  const parsed = parseInt(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

function parseFloatOrDefault(value, defaultValue) {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}
